import React, { useEffect, useState, ReactNode, useImperativeHandle, forwardRef } from "react";
import { useTranslation } from "react-i18next";
type InputInlinePropsType = {
  value?: any;
  type?: string;
  title?: string;
  testId?: string;
  placeholder?: string;
  autoFocus?: boolean;
  border?: boolean;
  readOnly?: boolean;
  error?: boolean;
  onFinish?: (value: string, save?: boolean) => void;
  onChange?: (value: string) => void;
  onClick?: () => void;
  onEdit?: (value: boolean) => void;
  onBlur?: () => void;
  rightAction?: ReactNode;
  /** Style variant: 'default' (original style) or 'compact' (compact style for detail views) */
  variant?: "default" | "inline";
};

export interface InputInlineRef {
  save: () => void;
}

const InputInline = forwardRef<InputInlineRef, InputInlinePropsType>((props, ref) => {
  const { t } = useTranslation();
  const {
    onFinish,
    value = "",
    testId,
    placeholder,
    autoFocus = false,
    error = false,
    border = false,
    onEdit = () => {},
    onChange = () => {},
    onBlur,
    rightAction,
    readOnly = false,
    type = "text",
    variant = "default",
  } = props;

  const [inputValue, setInputValue] = useState(value);

  const handleOnChange = (e) => {
    setInputValue(e.target.value);
    onChange(e.target.value);
    onEdit(true);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleOnFinish();
    }
  };

  const handleOnFinish = () => {
    if (inputValue !== value && onFinish) {
      onFinish(inputValue, true);
      onEdit(false);
    }
  };

  const handleBlur = () => {
    handleOnFinish();
    if (onBlur) {
      onBlur();
    }
  };

  // Expose save method via ref
  useImperativeHandle(ref, () => ({
    save: handleOnFinish,
  }));

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Style for default variant (original style)
  const defaultBorderStyle = border ? "outline-1 -outline-offset-1 outline-gray-300" : "";
  const defaultErrorStyle = error
    ? " text-red-900 outline-1 -outline-offset-1 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
    : "";
  const defaultFocusStyle = error ? "" : "focus:outline-blue-500";

  // Style for compact variant (compact style for detail views)
  const isCompactVariant = variant === "inline";
  const compactContainerClass = isCompactVariant
    ? "flex items-center w-full rounded-md border border-gray-300 px-2 py-1 min-h-[28px] focus-within:border-blue-500"
    : `flex items-center w-full rounded-md ${defaultBorderStyle} ${defaultErrorStyle}`;

  const compactInputClass = isCompactVariant
    ? "block w-full bg-white text-[12px] leading-4 font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none"
    : `block w-full rounded-md bg-white px-3 py-1.5 text-sm font-medium leading-5 text-gray-800 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 ${defaultFocusStyle} ${
        readOnly ? "cursor-not-allowed" : ""
      }`;

  return (
    <div className={compactContainerClass}>
      <input
        type={type}
        data-testid={testId}
        className={compactInputClass}
        value={inputValue}
        placeholder={placeholder ? t(placeholder) : undefined}
        onChange={handleOnChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyPress}
        autoFocus={autoFocus}
        readOnly={readOnly}
      />
      {rightAction}
    </div>
  );
});

InputInline.displayName = "InputInline";

export default InputInline;
